import { useState } from 'react'
import { PageContainer, ProTable, type ProColumns } from '@ant-design/pro-components'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Alert, App, Button, Space, Tag, Typography, type MenuProps, type TableProps } from 'antd'
import { ChevronDown, ListChecks, Plus } from 'lucide-react'
import type { ContractTemplateType } from '@super-mega/server/contracts/contract'
import type { AccountDisplay } from '@super-mega/server/contracts/profile'
import { TableRowActions } from '@/components/table-row-actions.tsx'
import { CREATED_AT_SORT_DIRECTIONS, DEFAULT_PAGE_SEARCH as DEFAULT_QUERY } from '@/const/list.ts'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import { AuthorizedDropdown } from '@/features/authorization/authorized-dropdown.tsx'
import { Authorized } from '@/features/authorization/authorized.tsx'
import {
  CONTRACT_TEMPLATE_TYPES,
  CONTRACT_TEMPLATE_VERSION_STATUS,
  contractTemplateSearchSchema,
  ContractTemplateVersionsDrawer,
  type ContractTemplateRecord
} from '@/features/contract/index.ts'
import { UserAvatarName } from '@/features/profile/index.ts'
import {
  useMemberCreatorOptions,
  usePageTableNavigation,
  type DateRangeFormValue,
  type PageTableSearch
} from '@/hooks/index.ts'
import { usePersistedListQuery } from '@/hooks/use-persisted-list-query.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { createListQueryPersistence } from '@/utils/list-query-persistence.ts'
import { getCreatedAtSearch, getCreatedAtSortOrder, getDateRangeFormValue } from '@/utils/list-query.ts'
import { proRemoteSearch } from '@/utils/pro-select-search.ts'

type TemplateQuery = PageTableSearch & {
  id?: number
  keyword?: string
  creatorMemberId?: number
  createdFrom?: string
  createdTo?: string
  sortField?: 'createdAt'
  sortOrder?: 'asc' | 'desc'
  type?: ContractTemplateType
  status?: 'enabled' | 'disabled'
}

type TemplateSearchValues = Pick<TemplateQuery, 'creatorMemberId' | 'keyword' | 'type' | 'status'> & {
  createdAtRange?: DateRangeFormValue
}

type TemplateRow = ContractTemplateRecord & {
  versionsCount: number
  creator: AccountDisplay
  createdAt: Date
  updatedAt: Date
  latestVersion: (NonNullable<ContractTemplateRecord['latestVersion']> & { createdAt: Date; updatedAt: Date }) | null
}

const listQuery = createListQueryPersistence('super-mega:contracts:templates:list-query', contractTemplateSearchSchema)

export const Route = createFileRoute('/_authenticated/contracts/templates/')({
  beforeLoad: listQuery.restore,
  validateSearch: listQuery.validateSearch,
  component: ContractTemplatesPage
})

function ContractTemplatesPage() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { message, modal } = App.useApp()
  const { currentSession } = Route.useRouteContext()
  const navigate = Route.useNavigate()
  const { search: query, navigateSearch } = usePersistedListQuery({
    persistence: listQuery,
    raw: Route.useSearch(),
    accountId: currentSession.id,
    navigate
  })
  const [versioning, setVersioning] = useState<TemplateRow>()
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<number[]>([])
  const canManage =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some(
      (permission) => permission.startsWith('contract.template.') && !permission.endsWith('.read')
    )
  const templates = useQuery({ ...trpc.contract.template.list.queryOptions(query), placeholderData: keepPreviousData })
  const memberCreator = useMemberCreatorOptions('contract.template.read', query.creatorMemberId)
  const publish = useMutation(trpc.contract.template.publishVersion.mutationOptions())
  const setEnabled = useMutation(trpc.contract.template.setEnabled.mutationOptions())
  const batchSetEnabled = useMutation(trpc.contract.template.batchSetEnabled.mutationOptions())
  const remove = useMutation(trpc.contract.template.remove.mutationOptions())
  const batchRemove = useMutation(trpc.contract.template.batchRemove.mutationOptions())
  const copyTemplateMutation = useMutation(trpc.contract.template.copy.mutationOptions())
  const table = usePageTableNavigation<TemplateQuery, TemplateSearchValues>({
    defaultSearch: DEFAULT_QUERY,
    getFormInitialValues: (search) => ({
      createdAtRange: getDateRangeFormValue(search.createdFrom, search.createdTo),
      creatorMemberId: search.creatorMemberId,
      keyword: search.keyword ?? (search.id === undefined ? undefined : String(search.id)),
      status: search.status,
      type: search.type
    }),
    getSearchFromForm: (values) => ({
      ...getCreatedAtSearch(values.createdAtRange),
      creatorMemberId: values.creatorMemberId,
      id: undefined,
      keyword: values.keyword?.trim() || undefined,
      type: values.type,
      status: values.status
    }),
    navigate: navigateSearch,
    onQueryChange: () => {
      setVersioning(undefined)
      setSelectedTemplateIds([])
    },
    search: query,
    showTotal: (total) => `共 ${total} 个模板`
  })

  const handleTableChange: NonNullable<TableProps<TemplateRow>['onChange']> =
    /* eslint-disable max-params -- Ant Design Table 的回调签名由组件定义。 */
    (_pagination, _filters, sorter, extra) => {
      if (extra.action !== 'sort') {
        return
      }
      const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter
      const sortOrder = getCreatedAtSortOrder(currentSorter.order)
      setVersioning(undefined)
      setSelectedTemplateIds([])
      void navigate({
        replace: true,
        search: (previous) => ({
          ...previous,
          current: 1,
          sortField: 'createdAt',
          sortOrder
        })
      })
    }
  /* eslint-enable max-params */

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries(trpc.contract.template.list.queryFilter()),
      queryClient.invalidateQueries(trpc.contract.template.options.queryFilter())
    ])

  const publishTemplate = async (row: TemplateRow) => {
    if (!row.latestVersion) {
      return
    }
    try {
      await publish.mutateAsync({ id: row.latestVersion.id })
      void message.success(`模板 v${row.latestVersion.version} 已发布`)
      await invalidate()
    } catch (error) {
      void message.error(getErrorMessage(error, '模板发布失败'))
    }
  }

  const changeTemplateStatus = async (row: TemplateRow) => {
    try {
      await setEnabled.mutateAsync({ id: row.id, isEnabled: !row.isEnabled })
      void message.success(row.isEnabled ? '合同模板已停用' : '合同模板已启用')
      await invalidate()
    } catch (error) {
      void message.error(getErrorMessage(error, '合同模板状态更新失败'))
    }
  }

  const changeSelectedStatus = async (isEnabled: boolean) => {
    try {
      const result = await batchSetEnabled.mutateAsync({ ids: selectedTemplateIds, isEnabled })
      setSelectedTemplateIds([])
      void message.success(`已${isEnabled ? '启用' : '停用'} ${result.count} 个合同模板`)
      await invalidate()
    } catch (error) {
      void message.error(getErrorMessage(error, '批量更新合同模板状态失败'))
    }
  }

  const removeTemplate = async (row: TemplateRow) => {
    try {
      await remove.mutateAsync({ id: row.id })
      void message.success('合同模板已删除')
      if (templates.data?.data.length === 1 && query.current > 1) {
        await navigateSearch({ search: (previous) => ({ ...previous, current: previous.current - 1 }) })
      } else {
        await invalidate()
      }
    } catch (error) {
      void message.error(getErrorMessage(error, '合同模板删除失败'))
    }
  }

  const copyTemplate = async (row: TemplateRow) => {
    try {
      const copied = await copyTemplateMutation.mutateAsync({ id: row.id })
      void message.success(`已复制为「${copied.name}」，仅带入当前最新版本`)
      await invalidate()
    } catch (error) {
      void message.error(getErrorMessage(error, '合同模板复制失败'))
    }
  }

  const confirmPublishTemplate = (row: TemplateRow) => {
    modal.confirm({
      onOk: () => publishTemplate(row),
      title: '发布最新草稿版本？'
    })
  }

  const confirmCopyTemplate = (row: TemplateRow) => {
    modal.confirm({
      content: '只复制当前最新版本正文，不会复制全部历史版本。复制后会生成一个新的草稿模板。',
      onOk: () => copyTemplate(row),
      title: '确认复制合同模板？'
    })
  }

  const confirmChangeTemplateStatus = (row: TemplateRow) => {
    modal.confirm({
      content: row.isEnabled ? '停用后不再参与新的合同生成，历史合同不受影响。' : undefined,
      okButtonProps: row.isEnabled ? { danger: true } : undefined,
      onOk: () => changeTemplateStatus(row),
      title: `确认${row.isEnabled ? '停用' : '启用'}模板？`
    })
  }

  const confirmRemoveTemplate = (row: TemplateRow) => {
    modal.confirm({
      content: '删除后模板不再出现在可选列表，历史合同仍会保留。',
      okButtonProps: { danger: true },
      onOk: () => removeTemplate(row),
      title: '确认删除模板？'
    })
  }

  const removeSelectedTemplates = async () => {
    try {
      const result = await batchRemove.mutateAsync({ ids: selectedTemplateIds })
      const allVisibleRowsSelected =
        selectedTemplateIds.length === (templates.data?.data.length ?? 0) && selectedTemplateIds.length > 0
      setSelectedTemplateIds([])
      void message.success(`已删除 ${result.count} 个合同模板`)
      if (allVisibleRowsSelected && query.current > 1) {
        await navigateSearch({ search: (previous) => ({ ...previous, current: previous.current - 1 }) })
      } else {
        await invalidate()
      }
    } catch (error) {
      void message.error(getErrorMessage(error, '批量删除合同模板失败'))
    }
  }

  const confirmChangeSelectedStatus = (isEnabled: boolean) => {
    if (isEnabled) {
      void changeSelectedStatus(true)
      return
    }
    modal.confirm({
      content: '停用后，选中的合同模板将不再参与新的合同生成，历史合同不受影响。',
      okButtonProps: { danger: true },
      okText: '确认停用',
      onOk: () => changeSelectedStatus(false),
      title: `停用已选 ${selectedTemplateIds.length} 个合同模板？`
    })
  }

  const confirmRemoveSelectedTemplates = () => {
    modal.confirm({
      content: '删除后将从模板列表和可选模板中移除，历史合同及版本记录仍会保留。',
      okButtonProps: { danger: true },
      okText: '确认删除',
      onOk: removeSelectedTemplates,
      title: `删除已选 ${selectedTemplateIds.length} 个合同模板？`
    })
  }

  const batchMenu: MenuProps = {
    items: [
      { key: 'enable', label: '批量启用' },
      { danger: true, key: 'disable', label: '批量停用' },
      { type: 'divider' as const },
      { danger: true, key: 'delete', label: '批量删除' }
    ],
    onClick: ({ key }) => {
      if (key === 'delete') {
        confirmRemoveSelectedTemplates()
        return
      }
      confirmChangeSelectedStatus(key === 'enable')
    }
  }

  const columns: ProColumns<TemplateRow>[] = [
    {
      dataIndex: 'keyword',
      fieldProps: { allowClear: true, placeholder: '模板名称、编码或 ID' },
      hideInTable: true,
      title: '关键词 / ID'
    },
    {
      dataIndex: 'creatorMemberId',
      fieldProps: {
        allowClear: true,
        loading: memberCreator.isLoading,
        options: memberCreator.options,
        placeholder: '选择创建人',
        ...proRemoteSearch(memberCreator.onSearch)
      },
      hideInTable: true,
      title: '创建人',
      valueType: 'select'
    },
    {
      dataIndex: 'createdAtRange',
      hideInTable: true,
      title: '创建时间',
      fieldProps: { presets: RANGE_PRESETS },
      valueType: 'dateRange'
    },
    {
      copyable: true,
      dataIndex: 'id',
      renderText: (value?: number) => (value === undefined ? '—' : String(value)),
      search: false,
      title: 'ID',
      width: '7rem'
    },
    {
      dataIndex: 'name',
      render: (_, row) => (
        <Space className="w-full min-w-0" orientation="vertical" size={0}>
          <Link
            className="block max-w-full truncate"
            params={{ templateId: String(row.id) }}
            search={{}}
            target="_blank"
            rel="noopener noreferrer"
            to="/contracts/templates/$templateId"
          >
            {row.name}
          </Link>
          <Typography.Text className="block max-w-full truncate" type="secondary">
            {row.description || '暂无模板说明'}
          </Typography.Text>
        </Space>
      ),
      search: false,
      title: '模板名称与说明',
      width: '20rem'
    },
    {
      dataIndex: 'type',
      render: (_, row) => (
        <Tag color={CONTRACT_TEMPLATE_TYPES[row.type].color}>{CONTRACT_TEMPLATE_TYPES[row.type].label}</Tag>
      ),
      title: '合同类型',
      valueEnum: Object.fromEntries(
        Object.entries(CONTRACT_TEMPLATE_TYPES).map(([value, item]) => [value, { text: item.label }])
      ),
      width: '8rem'
    },
    {
      dataIndex: 'currentVersion',
      render: (_, row) => (row.currentVersion > 0 ? `v${row.currentVersion}` : '未发布'),
      search: false,
      title: '当前版本',
      width: '7rem'
    },
    {
      dataIndex: 'latestVersion',
      render: (_, row) =>
        row.latestVersion ? (
          <Tag color={CONTRACT_TEMPLATE_VERSION_STATUS[row.latestVersion.status].color}>
            v{row.latestVersion.version} · {CONTRACT_TEMPLATE_VERSION_STATUS[row.latestVersion.status].label}
          </Tag>
        ) : (
          '—'
        ),
      search: false,
      title: '最新草稿',
      width: '10rem'
    },
    {
      dataIndex: 'status',
      hideInTable: true,
      title: '状态',
      valueEnum: { enabled: { text: '启用' }, disabled: { text: '已停用' } }
    },
    {
      dataIndex: 'isEnabled',
      render: (_, row) => <Tag color={row.isEnabled ? 'success' : 'default'}>{row.isEnabled ? '启用' : '已停用'}</Tag>,
      search: false,
      title: '状态',
      width: '6rem'
    },
    {
      dataIndex: 'versionsCount',
      renderText: (value?: number) => `${value ?? 0} 个版本`,
      search: false,
      title: '版本数',
      width: '7rem'
    },
    {
      dataIndex: 'creator',
      render: (_, row) => <UserAvatarName avatar={row.creator.avatar} name={row.creator.name} size={28} />,
      search: false,
      title: '创建人',
      width: '10rem'
    },
    {
      dataIndex: 'createdAt',
      search: false,
      sortOrder: query.sortOrder === 'asc' ? 'ascend' : 'descend',
      sorter: true,
      sortDirections: CREATED_AT_SORT_DIRECTIONS,
      title: '创建时间',
      valueType: 'dateTime',
      width: '12rem'
    },
    {
      dataIndex: 'updatedAt',
      search: false,
      title: '最近更新',
      valueType: 'dateTime',
      width: '12rem'
    },
    {
      align: 'center',
      fixed: 'right',
      render: (_, row) =>
        canManage ? (
          <TableRowActions
            resource="contract.template"
            actions={[
              {
                key: 'edit',
                label: '编辑',
                render: (
                  <Link
                    to="/contracts/templates/$templateId"
                    params={{ templateId: String(row.id) }}
                    search={{}}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Authorized permissions={['contract.template.update']}>
                      <Button size="small" type="link">
                        编辑
                      </Button>
                    </Authorized>
                  </Link>
                )
              },
              { key: 'versions', label: '版本', onClick: () => setVersioning(row) },
              ...(row.latestVersion?.status === 'draft'
                ? [{ key: 'publish', label: '发布', onClick: () => confirmPublishTemplate(row) }]
                : []),
              { key: 'copy', label: '复制', onClick: () => confirmCopyTemplate(row) },
              {
                danger: row.isEnabled,
                key: 'status',
                label: row.isEnabled ? '停用' : '启用',
                onClick: () => confirmChangeTemplateStatus(row)
              },
              { danger: true, key: 'delete', label: '删除', onClick: () => confirmRemoveTemplate(row) }
            ]}
          />
        ) : (
          '—'
        ),
      search: false,
      title: '操作',
      valueType: 'option',
      width: '10rem'
    }
  ]

  return (
    <PageContainer
      breadcrumbRender={false}
      content="管理合同正文、版本与发布状态；已发布版本用于后续生成合同。"
      title="合同模板"
    >
      {templates.isError ? (
        <Alert
          action={
            <Button onClick={() => void templates.refetch()} size="small">
              重试
            </Button>
          }
          className="mb-4"
          showIcon
          title={getErrorMessage(templates.error, '合同模板加载失败')}
          type="error"
        />
      ) : null}
      <ProTable<TemplateRow, TemplateQuery>
        columns={columns}
        columnsState={{ persistenceKey: 'contracts-templates:table', persistenceType: 'localStorage' }}
        dataSource={templates.data?.data ?? []}
        loading={{ delay: 150, spinning: templates.isFetching }}
        onChange={handleTableChange}
        onReset={table.onReset}
        onSubmit={table.onSubmit}
        options={{ reload: () => void templates.refetch() }}
        pagination={{ ...table.pagination, total: templates.data?.total ?? 0 }}
        formRef={table.formRef}
        rowKey="id"
        rowSelection={
          canManage
            ? {
                onChange: (keys) => setSelectedTemplateIds(keys.map(Number)),
                selectedRowKeys: selectedTemplateIds
              }
            : undefined
        }
        scroll={{ x: '110rem' }}
        form={{ initialValues: table.formInitialValues }}
        search={{ defaultCollapsed: false, labelWidth: 'auto' }}
        size="small"
        toolBarRender={() =>
          canManage
            ? [
                <AuthorizedDropdown
                  resource="contract.template"
                  key="batch-actions"
                  menu={batchMenu}
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <Button disabled={selectedTemplateIds.length === 0} icon={<ListChecks aria-hidden size={16} />}>
                    批量操作 <ChevronDown aria-hidden size={14} />
                  </Button>
                </AuthorizedDropdown>,
                <Authorized key="new" permissions={['contract.template.create']}>
                  <Button
                    icon={<Plus aria-hidden size={16} />}
                    key="new"
                    onClick={() =>
                      void navigate({
                        to: '/contracts/templates/$templateId',
                        params: { templateId: 'new' },
                        search: {},
                        state: { contractTemplateReturnSearch: query }
                      })
                    }
                    type="primary"
                  >
                    新建模板
                  </Button>
                </Authorized>
              ]
            : []
        }
      />
      <ContractTemplateVersionsDrawer
        onClose={() => setVersioning(undefined)}
        onEditDraft={(template) => {
          setVersioning(undefined)
          void navigate({
            to: '/contracts/templates/$templateId',
            params: { templateId: String(template.id) },
            search: {},
            state: { contractTemplateReturnSearch: query }
          })
        }}
        onViewVersion={(_, versionId) => {
          setVersioning(undefined)
          void navigate({
            to: '/contracts/templates/versions/$versionId',
            params: { versionId: String(versionId) },
            search: {},
            state: { contractTemplateReturnSearch: query }
          })
        }}
        template={versioning}
      />
    </PageContainer>
  )
}
